import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, ShieldCheck } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPassword = ({ theme }) => {
  usePageTitle('Nova Senha');
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const token = searchParams.get('token');

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    if (!token) {
      setError('Token não fornecido');
      setValidating(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/validate-reset-token?token=${token}`);
      setTokenValid(true);
      setEmail(response.data.email);
    } catch (err) {
      setError('Link inválido ou expirado');
      setTokenValid(false);
    } finally {
      setValidating(false);
    }
  };

  const validatePassword = () => {
    if (password.length < 8) {
      return 'A senha deve ter no mínimo 8 caracteres';
    }
    if (!/[A-Z]/.test(password)) {
      return 'A senha deve conter pelo menos uma letra maiúscula';
    }
    if (!/[a-z]/.test(password)) {
      return 'A senha deve conter pelo menos uma letra minúscula';
    }
    if (!/[0-9]/.test(password)) {
      return 'A senha deve conter pelo menos um número';
    }
    if (!/[!@#$%^&*]/.test(password)) {
      return 'A senha deve conter pelo menos um caractere especial (!@#$%^&*)';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate password
    const passwordError = validatePassword();
    if (passwordError) {
      setError(passwordError);
      return;
    }

    // Check if passwords match
    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = () => {
    if (password.length === 0) return { label: '', color: '', width: '0%' };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[!@#$%^&*]/.test(password)) strength++;

    if (strength <= 2) return { label: 'Fraca', color: 'bg-red-500', width: '33%' };
    if (strength <= 4) return { label: 'Média', color: 'bg-yellow-500', width: '66%' };
    return { label: 'Forte', color: 'bg-green-500', width: '100%' };
  };

  if (validating) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${
        theme === 'dark' ? 'bg-[#0a0a0a]' : 'bg-gray-50'
      }`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
            Validando link...
          </p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 transition-colors duration-300 ${
        theme === 'dark' ? 'bg-[#0a0a0a]' : 'bg-gray-50'
      }`}>
        <div className={`max-w-md w-full p-8 rounded-2xl shadow-xl text-center ${
          theme === 'dark' ? 'bg-[#1a1a1a]' : 'bg-white'
        }`}>
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className={`text-2xl font-bold mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Link Inválido ou Expirado
          </h2>
          <p className={`mb-6 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Este link de recuperação de senha não é mais válido. Por favor, solicite um novo link.
          </p>
          <button
            onClick={() => navigate('/forgot-password')}
            className="w-full px-6 py-3 rounded-xl font-medium transition-all duration-300 bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-lg mb-3"
          >
            Solicitar Novo Link
          </button>
          <button
            onClick={() => navigate('/login')}
            className={`w-full px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
              theme === 'dark'
                ? 'bg-[#0a0a0a] text-gray-400 hover:bg-gray-800'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Voltar ao Login
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 transition-colors duration-300 ${
        theme === 'dark' ? 'bg-[#0a0a0a]' : 'bg-gray-50'
      }`}>
        <div className={`max-w-md w-full p-8 rounded-2xl shadow-xl ${
          theme === 'dark' ? 'bg-[#1a1a1a]' : 'bg-white'
        }`}>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className={`text-2xl font-bold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Senha Alterada com Sucesso! ✅
            </h2>
            <p className={`mb-6 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Sua senha foi redefinida. Você já pode fazer login com sua nova senha.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="w-full px-6 py-3 rounded-xl font-medium transition-all duration-300 bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-lg"
            >
              Fazer Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  const passwordStrength = getPasswordStrength();

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 transition-colors duration-300 ${
      theme === 'dark' ? 'bg-[#0a0a0a]' : 'bg-gray-50'
    }`}>
      <div className={`max-w-md w-full p-8 rounded-2xl shadow-xl ${
        theme === 'dark' ? 'bg-[#1a1a1a]' : 'bg-white'
      }`}>
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 mb-4">
            <ShieldCheck className="w-8 h-8 text-white" />
          </div>
          <h2 className={`text-2xl font-bold mb-2 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Redefinir Senha
          </h2>
          <p className={`text-sm ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Email: <strong>{email}</strong>
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* New Password */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Nova Senha
            </label>
            <div className="relative">
              <Lock className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
              }`} />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Digite sua nova senha"
                required
                className={`w-full pl-12 pr-12 py-3 rounded-xl border-2 transition-all duration-300 ${
                  theme === 'dark'
                    ? 'bg-[#0a0a0a] border-gray-700 text-white placeholder-gray-500 focus:border-purple-500'
                    : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-purple-500'
                } focus:outline-none`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2"
              >
                {showPassword ? (
                  <EyeOff className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} size={20} />
                ) : (
                  <Eye className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} size={20} />
                )}
              </button>
            </div>
            
            {/* Password Strength Indicator */}
            {password && (
              <div className="mt-2">
                <div className="flex justify-between items-center mb-1">
                  <span className={`text-xs ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    Força da senha:
                  </span>
                  <span className={`text-xs font-medium ${
                    passwordStrength.label === 'Forte' ? 'text-green-600' :
                    passwordStrength.label === 'Média' ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {passwordStrength.label}
                  </span>
                </div>
                <div className={`h-2 rounded-full overflow-hidden ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`}>
                  <div
                    className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                    style={{ width: passwordStrength.width }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Confirmar Nova Senha
            </label>
            <div className="relative">
              <Lock className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
              }`} />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirme sua nova senha"
                required
                className={`w-full pl-12 pr-12 py-3 rounded-xl border-2 transition-all duration-300 ${
                  theme === 'dark'
                    ? 'bg-[#0a0a0a] border-gray-700 text-white placeholder-gray-500 focus:border-purple-500'
                    : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-purple-500'
                } focus:outline-none`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2"
              >
                {showConfirmPassword ? (
                  <EyeOff className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} size={20} />
                ) : (
                  <Eye className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} size={20} />
                )}
              </button>
            </div>
          </div>

          {/* Password Requirements */}
          <div className={`p-4 rounded-xl ${
            theme === 'dark' ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'
          }`}>
            <p className={`text-xs font-medium mb-2 ${
              theme === 'dark' ? 'text-blue-300' : 'text-blue-800'
            }`}>
              Requisitos da senha:
            </p>
            <ul className={`text-xs space-y-1 ${
              theme === 'dark' ? 'text-blue-400' : 'text-blue-700'
            }`}>
              <li className="flex items-center gap-2">
                <span className={password.length >= 8 ? 'text-green-500' : ''}>
                  {password.length >= 8 ? '✓' : '○'}
                </span>
                Mínimo 8 caracteres
              </li>
              <li className="flex items-center gap-2">
                <span className={/[A-Z]/.test(password) ? 'text-green-500' : ''}>
                  {/[A-Z]/.test(password) ? '✓' : '○'}
                </span>
                Uma letra maiúscula
              </li>
              <li className="flex items-center gap-2">
                <span className={/[a-z]/.test(password) ? 'text-green-500' : ''}>
                  {/[a-z]/.test(password) ? '✓' : '○'}
                </span>
                Uma letra minúscula
              </li>
              <li className="flex items-center gap-2">
                <span className={/[0-9]/.test(password) ? 'text-green-500' : ''}>
                  {/[0-9]/.test(password) ? '✓' : '○'}
                </span>
                Um número
              </li>
              <li className="flex items-center gap-2">
                <span className={/[!@#$%^&*]/.test(password) ? 'text-green-500' : ''}>
                  {/[!@#$%^&*]/.test(password) ? '✓' : '○'}
                </span>
                Um caractere especial (!@#$%^&*)
              </li>
            </ul>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:shadow-lg'
            } text-white`}
          >
            {loading ? 'Redefinindo...' : 'Redefinir Senha'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
